import moment, { Moment } from 'moment';
import React from 'react';
import { IntlShape } from 'react-intl';
import styled from 'styled-components';
import { TimeOffType, TimeoffResponse } from 'api/timeoff/types';
import { formatterWithMinutes } from 'components/atoms/input-duration';
import { WorkingDaysConfiguration } from 'components/organisms/user-capacity';
import { getDays } from './date-field/get-days-array';

const TimeOffNotesStyled = styled.div`
  .timeoff-note {
    margin-top: 8px;
    font-weight: 400;
    font-size: 12px;
    color: var(--gray-80);
    font-style: italic;
    line-height: 16px;
    color: var(--gray-80);
    max-width: 300px;
    font-style: italic;
    white-space: normal;
    word-break: break-word;
    &::before {
      content: '"';
    }
    &::after {
      content: '"';
    }
  }
`;

export const numberOfWorkingDaysBetweenDatesForTimeoff = (
  startDate: Moment,
  endDate: Moment,
  workingDays: WorkingDaysConfiguration,
): number => {
  const totalNoOfDays = endDate.diff(startDate, 'day') + 1;

  return [...Array(totalNoOfDays)].reduce((noOfDays, _, index) => {
    const dayName = moment(startDate)
      .add(index, 'day')
      .locale('en')
      .format('dddd');

    return workingDays[dayName] ? noOfDays + 1 : noOfDays;
  }, 0);
};

export const checkIfCurrentDateHasTimeOff = (
  timeOffArray?: TimeoffResponse[],
  date = moment(),
): TimeoffResponse | undefined => {
  if (timeOffArray == null || timeOffArray.length === 0) {
    return undefined;
  }

  return timeOffArray.find((timeOff) =>
    date.isBetween(
      moment(timeOff.timeOffStart).startOf('day'),
      moment(timeOff.timeOffEnd).endOf('day'),
      undefined,
      '[]',
    ),
  );
};

export const getTimeOffArray = (
  startDate: Moment | undefined,
  endDate: Moment,
  userTimeOffArray: TimeoffResponse[] | undefined,
): TimeoffResponse[] | undefined => {
  if (startDate == null || endDate == null) {
    return undefined;
  }

  return userTimeOffArray
    ?.filter(
      (timeOffT) =>
        moment(timeOffT.timeOffStart).isBetween(
          startDate.startOf('day'),
          endDate.endOf('day'),
          undefined,
          '[]',
        ) ||
        moment(timeOffT.timeOffEnd).isBetween(
          startDate.startOf('day'),
          endDate.endOf('day'),
          undefined,
          '[]',
        ) ||
        moment(startDate).isBetween(
          moment(timeOffT.timeOffStart).startOf('day'),
          moment(timeOffT.timeOffEnd).endOf('day'),
          undefined,
          '[]',
        ) ||
        moment(endDate).isBetween(
          moment(timeOffT.timeOffStart).startOf('day'),
          moment(timeOffT.timeOffEnd).endOf('day'),
          undefined,
          '[]',
        ),
    )
    .sort((a, b) => {
      return moment.utc(a.timeOffStart).diff(moment.utc(b.timeOffStart));
    });
};

export const getTimeOffDaysInRange = (
  startDate: Moment,
  endDate: Moment,
  userTimeOffArray: TimeoffResponse[] | undefined,
  workingDaysConfig: WorkingDaysConfiguration,
): number => {
  let countOfDays = 0;

  userTimeOffArray?.forEach((timeOff) => {
    const newStartDate = moment(timeOff.timeOffStart).isBefore(startDate, 'day')
      ? startDate
      : moment(timeOff.timeOffStart).startOf('day');
    const newEndDate = moment(timeOff.timeOffEnd).isAfter(endDate, 'day')
      ? endDate
      : moment(timeOff.timeOffEnd).endOf('day');

    countOfDays += numberOfWorkingDaysBetweenDatesForTimeoff(
      newStartDate,
      newEndDate,
      workingDaysConfig,
    );
  });

  return countOfDays;
};

export const getTimeOffDaysInAWeek = (
  startDate: Moment,
  userTimeOffArray: TimeoffResponse[] | undefined,
): number => {
  const endDate = startDate.add(5, 'days');
  const timeOffInWeek = getTimeOffArray(startDate, endDate, userTimeOffArray);
  const days = getDays(startDate, 5);

  let countOfDays = 0;

  timeOffInWeek?.forEach((timeOff) => {
    days.forEach((day) => {
      if (
        moment(day).isBetween(
          moment(timeOff.timeOffStart).startOf('day'),
          moment(timeOff.timeOffEnd).endOf('day'),
          undefined,
          '[]',
        )
      ) {
        countOfDays += 1;
      }
    });
  });

  return countOfDays;
};

export const getTimeOffMinutesInAWeek = (
  startDate: Moment,
  userTimeOffArray: TimeoffResponse[] | undefined,
): number => {
  const endDate = startDate.add(5, 'days');
  const timeOffInWeek = getTimeOffArray(startDate, endDate, userTimeOffArray);

  let timeOffMinutes = 0;

  timeOffInWeek?.forEach((timeOff) => {
    timeOffMinutes += timeOff.timeOffDuration;
  });

  return timeOffMinutes;
};

export const getStartDate = (dueDate) => {
  if (dueDate == null || moment().diff(dueDate, 'days') >= 1) {
    return undefined;
  }
  if (moment(dueDate).diff(new Date(), 'days') > 14) {
    return moment(dueDate).subtract(2, 'weeks');
  }

  return moment();
};

export const getTimeoffType = ({
  timeoffDurationInMinutes,
  userCapacityPerDay,
}: {
  userCapacityPerDay: number;
  timeoffDurationInMinutes: number;
}): TimeOffType => {
  if (userCapacityPerDay === timeoffDurationInMinutes) {
    return TimeOffType.FullDay;
  }

  if (userCapacityPerDay / 2 === timeoffDurationInMinutes) {
    return TimeOffType.HalfDay;
  }

  return TimeOffType.Custom;
};

export const getTimeOffDurationPerDay = (
  timeOffDuration = 0,
  timeOffType: TimeOffType = TimeOffType.Custom,
  intl: IntlShape,
  holidayId?: number,
): string => {
  if (timeOffType === TimeOffType.FullDay || holidayId != null) {
    return intl.formatMessage({ id: 'timeoff.type.full_day' });
  }

  if (timeOffType === TimeOffType.HalfDay) {
    return intl.formatMessage({ id: 'timeoff.type.half_day' });
  }

  return formatterWithMinutes(timeOffDuration);
};

export const TimeoffNotes = ({
  timeOffNotes,
}: {
  timeOffNotes: {
    timeOffId: number;
    note: string;
  }[];
}) => {
  return (
    <TimeOffNotesStyled>
      {timeOffNotes?.map((note) => (
        <div className="timeoff-note" key={note.timeOffId}>
          {note.note}
        </div>
      ))}
    </TimeOffNotesStyled>
  );
};
